import {
  AdmCompany,
  AdmProject,
  AllMemberTypes,
} from "@custom-types/base-types";
import { useGenerateRoutes } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { getMemberDisplayName } from "@utils/data-display";
import { getAdminPanelApiUrl } from "@utils/url-utils";
import { useCallback } from "react";

interface ReturnProps {
  /** Copy company details */
  copyCompanyInfo: (company: AdmCompany) => Promise<void>;

  /** Copy project details */
  copyProjectInfo: (project: AdmProject) => Promise<void>;

  /** Copy member details */
  copyMemberInfo: (member: AllMemberTypes) => Promise<void>;
}

/** Hook that creates callbacks to copy subject info */
export function useSubjectClipboard(): ReturnProps {
  const { generateCompanyRoute, generateProjectRoute, generateMemberRoute } =
    useGenerateRoutes();

  const baseUrl = getAdminPanelApiUrl(
    runtimeConfig.urls.baseUrl,
    runtimeConfig.numberedEnv
  );

  const copyCompanyInfo = useCallback(
    async (company: AdmCompany) => {
      const clipboardString = `***Workspace Details***
**Workspace ID:** \`${company.id}\`
**Name:** ${company.name}
[Admin Panel Link]( ${baseUrl}${
        generateCompanyRoute({
          companyId: company.id,
        }).pathname
      } )`;

      return navigator.clipboard.writeText(clipboardString);
    },
    [baseUrl, generateCompanyRoute]
  );

  const copyProjectInfo = useCallback(
    async (project: AdmProject) => {
      const clipboardString = `***Project Details***
**Project ID:** \`${project.id}\`
**Name:** ${project.name}
[Admin Panel Link]( ${baseUrl}${
        generateProjectRoute({
          projectId: project.id,
        }).pathname
      } )`;

      return navigator.clipboard.writeText(clipboardString);
    },
    [baseUrl, generateProjectRoute]
  );

  const copyMemberInfo = useCallback(
    async (member: AllMemberTypes) => {
      const clipboardString = `***User Details***
**User ID:** \`${member.identity}\`
**Email:** \`${member.email}\`
**Name:** ${getMemberDisplayName(member)}
[Admin Panel Link]( ${baseUrl}${
        generateMemberRoute({
          memberId: member.identity,
        }).pathname
      } )`;

      return navigator.clipboard.writeText(clipboardString);
    },
    [baseUrl, generateMemberRoute]
  );

  return {
    copyCompanyInfo,
    copyProjectInfo,
    copyMemberInfo,
  };
}
